import React, { useState } from "react";
import { Input, Button, Icon, Popup } from "semantic-ui-react";
import { get } from "lodash";
import PropTypes from "prop-types";

// Modals
import ClientSelectorModal from "./ClientSelectorModal";
// Removed default NewClientModal usage

// Styles
import styles from "./styles.module.css";

/**
 * Componente reutilizable para selección de clientes
 * @param {Object} props
 * @param {Object} props.client - Cliente seleccionado actual
 * @param {Function} props.setClient - Función para establecer el cliente seleccionado
 * @param {Function} props.setAddresses - Función para establecer direcciones (opcional)
 * @param {Object} props.apiConfig - Configuración de endpoints de API
 * @param {string} props.apiConfig.searchClients - Endpoint para buscar clientes
 * @param {string} props.apiConfig.createClient - Endpoint para crear cliente
 * @param {Object} props.ui - Configuración de UI
 * @param {string} props.ui.placeholder - Placeholder del input
 * @param {string} props.ui.searchButtonText - Texto del botón de búsqueda
 * @param {boolean} props.ui.showCreateButton - Mostrar botón de crear cliente
 * @param {boolean} props.required - Campo requerido
 * @param {boolean} props.disabled - Campo deshabilitado
 * @param {Function} props.ref - Ref del input
 */

const StudentsSelector = React.forwardRef(
	(
		{
			client,
			setClient,
			setAddresses,
			apiConfig,
			ui = {},
			required = false,
			disabled = false,
			className = "",
			representativeId = null,
			showCourseSection = false,
			showUserSection = false,
			showParentRelationship = false,
			omitList = [],
			onCustomSearch,
			onCustomAdd,
			title,
			parentId = null,
			multiple = false,
			width = "60%",
		},
		ref
	) => {
		const [openClientSelector, setOpenClientSelector] = useState(false);
		// Container to mount a custom Add modal provided by parent via onCustomAdd
		const [customAddModal, setCustomAddModal] = useState(null);
		// Container to mount a custom Search modal provided by parent via onCustomSearch
		const [customSearchModal, setCustomSearchModal] = useState(null);
		// Controla si el popup de lista completa está visible (solo sobre el input de texto)
		const [showPopup, setShowPopup] = useState(false);
		// Local search text for typing when multiple selection is active
		const [searchText, setSearchText] = useState("");

		const defaultTitle = showCourseSection
			? "Seleccionar Curso"
			: showParentRelationship
			? "Seleccionar Padres"
			: "Seleccionar Estudiantes";
		const defaultPlaceholder = showCourseSection
			? "Buscar Cursos..."
			: showParentRelationship
			? "Buscar Padres..."
			: "Buscar Estudiantes...";

		// Configuración por defecto de UI
		const uiConfig = {
			...ui,
			searchButtonText: ui?.searchButtonText ?? "Buscar",
			showCreateButton: ui?.showCreateButton ?? true,
			searchButtonColor: ui?.searchButtonColor ?? "blue",
			createButtonColor: ui?.createButtonColor ?? "blue",
			placeholder: ui?.placeholder ?? defaultPlaceholder,
			title: title ?? ui?.title ?? defaultTitle,
		};

		// Configuración por defecto de API
		const defaultApiConfig = {
			searchClients: "/students",
			createClient: "/students",
			fields: ["id", "fullName", "code"], // campos por defecto para buscar
			orderKey: showCourseSection ? "name" : "fullName", // campo por defecto para ordenar
			...apiConfig,
		};

		const hasSelection = multiple ? Array.isArray(client) && client.length > 0 : !!client?.id;

		const onSearch = () => {
			if (hasSelection) {
				setClient(multiple ? [] : { name: "" });
				setSearchText("");
				if (setAddresses) {
					setAddresses([]);
				}
				if (ref?.current) {
					ref.current.focus();
				}
				return;
			}
			handleOpenClientSelector();
		};

		const handleOpenClientSelector = () => {
			if (!openClientSelector) {
				// Allow custom search modal if provided
				if (typeof onCustomSearch === "function") {
					const payload = {
						currentValue: client?.name || "",
						setClient: (c) => {
							setClient(c);
							if (setAddresses && c?.addresses) {
								const updatedAddresses = c.addresses.length === 1 ? [{ ...c.addresses[0], selected: true }] : c.addresses;
								setAddresses(updatedAddresses);
							}
						},
						// informar que este flujo espera un modal
						isModal: true,
						// Permitir al consumidor montar/desmontar cualquier modal aquí
						mount: (node) => {
							setCustomSearchModal(node);
						},
						unmount: () => {
							setCustomSearchModal(null);
						},
						openDefault: () => {
							setOpenClientSelector(true);
						},
						closeDefault: () => {
							setOpenClientSelector(false);
						},
						apiConfig: defaultApiConfig,
						uiConfig,
						representativeId,
						parentId,
						showCourseSection,
						showParentRelationship,
					};

					const result = onCustomSearch(payload);
					if (result && typeof result === "object" && result.$$typeof) {
						setCustomSearchModal(result);
					}
					return;
				}
				setOpenClientSelector(true);
			} else {
				setOpenClientSelector(false);
			}
		};

		const handleClientSelect = (selectedClient) => {
			// When selecting parents or multiple option, allow array selection
			if ((showParentRelationship || multiple) && Array.isArray(selectedClient)) {
				setClient(selectedClient);
				setOpenClientSelector(false);
				return;
			}
			setClient(selectedClient);
			setOpenClientSelector(false);
			// Si hay función para manejar direcciones y el cliente tiene direcciones
			if (setAddresses && selectedClient?.addresses) {
				const updatedAddresses =
					selectedClient.addresses.length === 1 ? [{ ...selectedClient.addresses[0], selected: true }] : selectedClient.addresses;
				setAddresses(updatedAddresses);
			}
		};

		// Función para manejar cuando el usuario hace clic fuera del input
		const handleInputBlur = () => {
			if (multiple) {
				if (searchText.trim() !== "") {
					setOpenClientSelector(true);
				}
			} else if (client?.name && !client?.id && client.name.trim() !== "") {
				// Si hay texto en el input pero no se ha seleccionado un cliente, abrir el modal
				setOpenClientSelector(true);
			}
		};

		// Función para limpiar solo el texto del input
		const handleClearSearch = () => {
			if (multiple) {
				setSearchText("");
			} else {
				setClient({ ...client, name: "" }); // Solo limpiar el texto del input
			}
		};

		// Función para obtener el valor a mostrar en el input
		const getInputDisplayValue = () => {
			if (multiple && Array.isArray(client)) {
				if (client.length === 0) return searchText;
				if (client.length >= 3) {
					const firstTwo = client
						.slice(0, 2)
						.map((c) => {
							return c.fullName || c.name || "";
						})
						.join(", ");
					const remainingCount = client.length - 2;
					return `${firstTwo} y ${remainingCount} más...`;
				}
				return client
					.map((c) => {
						return c.fullName || c.name || "";
					})
					.join(", ");
			}
			if (showParentRelationship && Array.isArray(client)) {
				return "";
			}
			if (client?.id && client?.tags && Array.isArray(client.tags) && client.tags.length > 0) {
				const tagNames = client.tags
					.map((tag) => {
						return tag.name;
					})
					.join(", ");
				return `${client.name} (${get(client, "clientType", "")})  - ${tagNames}`;
			}
			// Si showCourseSection es true y el client tiene section, mostrar nombre - sección
			if (showCourseSection && client?.section) {
				return `${client.name} - ${client.section}`;
			}

			if (showUserSection && client?.code) {
				return `${client.name} - ${client.code}`;
			}
			// Si showParentRelationship es true y el client tiene relationship, mostrar nombre - relación (para padres)
			if (showParentRelationship && (client?.relationship || client?.fullName)) {
				const displayName = client?.fullName || client?.name || "";
				if (displayName && client?.relationship) {
					return `${displayName} - ${client.relationship}`;
				}
			}
			return client?.name || "";
		};

		const inputClassName = hasSelection ? styles.disable : styles.search;
		const combinedClassName = `${inputClassName} ${className}`.trim();
		const isReadOnly = hasSelection || disabled;

		const handleCreateClick = () => {
			if (typeof onCustomAdd === "function") {
				const payload = {
					setClient: (c) => {
						setClient(c);
					},
					// informar que este flujo espera un modal
					isModal: true,
					mount: (node) => {
						setCustomAddModal(node);
					},
					unmount: () => {
						setCustomAddModal(null);
					},
					apiConfig: defaultApiConfig,
					uiConfig,
					representativeId,
					parentId,
				};
				// si onCustomAdd es una función, invocarla con el payload
				onCustomAdd(payload);
				// si onCustomAdd retorna un elemento React, montarlo automáticamente
				const result = onCustomAdd(payload);
				if (result && typeof result === "object" && result.$$typeof) {
					setCustomAddModal(result);
				}
			}
		};

		const popupContent =
			multiple && Array.isArray(client) ? (
				<div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "4px" }}>
					{client.map((c, i) => {
						return (
							<div
								key={c.id || i}
								style={{
									fontSize: "0.85rem",
									color: "#333",
									fontWeight: "500",
									whiteSpace: "nowrap",
								}}>
								{`${i + 1}. ${c.fullName || c.name || ""}`}
							</div>
						);
					})}
				</div>
			) : null;

		const renderInputComponent = () => {
			return (
				<Input
					value={getInputDisplayValue()}
					onChange={(e) => {
						if (multiple) {
							setSearchText(e.target.value);
						} else {
							setClient({ ...client, name: e.target.value });
						}
					}}
					className={combinedClassName}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							e.stopPropagation();
							onSearch();
						}
					}}
					onBlur={handleInputBlur}
					readOnly={isReadOnly}
					ref={ref}
					type="text"
					placeholder={uiConfig.placeholder}
					action
					fluid
					required={required}
					disabled={disabled}>
					<input
						onMouseEnter={() => {
							if (multiple && Array.isArray(client) && client.length >= 3) {
								setShowPopup(true);
							}
						}}
						onMouseLeave={() => {
							return setShowPopup(false);
						}}
					/>
					{(multiple ? !!searchText : client?.name && !client?.id) && (
						<button
							type="button"
							onClick={handleClearSearch}
							style={{
								position: "absolute",
								right: uiConfig.showCreateButton ? "115px" : "80px",
								top: "50%",
								transform: "translateY(-50%)",
								cursor: "pointer",
								padding: "5px",
								zIndex: 10,
								color: "#999",
								fontSize: "16px",
								background: "none",
								border: "none",
								borderRadius: "50%",
							}}
							onMouseEnter={(e) => {
								e.target.style.color = "#333";
							}}
							onMouseLeave={(e) => {
								e.target.style.color = "#999";
							}}>
							<Icon name="close" />
						</button>
					)}
					{uiConfig.showCreateButton && (
						<Button
							/* 	color={uiConfig.createButtonColor} */
							type="button"
							icon={<Icon name="plus" link inverted />}
							onClick={handleCreateClick}
							disabled={disabled}
							className={`${styles.inputButton} ${styles.createButton}`}
							styles={{ color: `${uiConfig.createButtonColor} !important` }}
						/>
					)}
					{multiple && hasSelection ? (
						<>
							<Button
								type="button"
								compact
								size="mini"
								icon={<Icon name="close" link inverted />}
								onClick={onSearch}
								disabled={disabled}
								className={`${styles.inputButton} ${styles.closeButton}`}
								title="Limpiar selección"
							/>
							<Button
								type="button"
								compact
								size="mini"
								icon={<Icon name="search" link />}
								onClick={handleOpenClientSelector}
								disabled={disabled}
								className={`${styles.inputButton} ${styles.searchButton}`}
								title="Buscar y agregar más"
							/>
						</>
					) : (
						<Button
							type="button"
							compact
							size="mini"
							icon={<Icon name={hasSelection ? "close" : "search"} link />}
							onClick={onSearch}
							disabled={disabled}
							className={`${styles.inputButton} ${hasSelection ? styles.closeButton : styles.searchButton}`}
						/>
					)}
				</Input>
			);
		};

		return (
			<>
				<div style={{ position: "relative" }}>
					{multiple && Array.isArray(client) && client.length >= 3 ? (
						<Popup
							open={showPopup}
							onClose={() => {
								return setShowPopup(false);
							}}
							trigger={<div style={{ display: "block", width: "100%" }}>{renderInputComponent()}</div>}
							content={popupContent}
							position="bottom left"
						/>
					) : (
						renderInputComponent()
					)}
				</div>
				{customAddModal}
				{customSearchModal}
				{openClientSelector && (
					<ClientSelectorModal
						title={uiConfig.title}
						setOpen={handleOpenClientSelector}
						setClient={handleClientSelect}
						search={multiple ? searchText : client?.name || ""}
						apiConfig={defaultApiConfig}
						ui={uiConfig}
						representativeId={representativeId}
						parentId={parentId}
						showCourseSection={showCourseSection}
						showParentRelationship={showParentRelationship}
						multiple={multiple}
						width={width}
						selectedClients={client}
						omitIds={[
							...omitList.map((p) => {
								return p && (p.id ?? p);
							}),
							...(multiple && Array.isArray(client)
								? client.map((c) => {
										return c && (c.id ?? c);
								  })
								: []),
						].filter(Boolean)}
					/>
				)}
			</>
		);
	}
);

StudentsSelector.propTypes = {
	client: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
	setClient: PropTypes.func.isRequired,
	setAddresses: PropTypes.func,
	representativeId: PropTypes.number,
	omitList: PropTypes.array,
	apiConfig: PropTypes.shape({
		searchClients: PropTypes.string,
		createClient: PropTypes.string,
		fields: PropTypes.arrayOf(PropTypes.string),
		orderKey: PropTypes.string,
		activeAttendance: PropTypes.number,
	}),
	ui: PropTypes.shape({
		placeholder: PropTypes.string,
		searchButtonText: PropTypes.string,
		showCreateButton: PropTypes.bool,
		searchButtonColor: PropTypes.string,
		createButtonColor: PropTypes.string,
	}),

	required: PropTypes.bool,
	disabled: PropTypes.bool,
	className: PropTypes.string,
	showCourseSection: PropTypes.bool,
	showUserSection: PropTypes.bool,
	showParentRelationship: PropTypes.bool,
	multiple: PropTypes.bool,
	onCustomSearch: PropTypes.func,
	onCustomAdd: PropTypes.func,
	title: PropTypes.string,
	parentId: PropTypes.number,
	width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

StudentsSelector.displayName = "StudentsSelector";

export default StudentsSelector;
